import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const EESSchedulingCalendar = () => {
  const currentDate = new Date();
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);

  // Mock session data
  const sessions = {
    '2025-01-25': [
      { id: 1, time: '10:00 AM', guest: 'Sarah Johnson', service: 'Single EES', status: 'confirmed' },
      { id: 2, time: '02:00 PM', guest: 'Michael Rodriguez', service: 'Weekly Package', status: 'pending' }
    ],
    '2025-01-26': [
      { id: 3, time: '09:00 AM', guest: 'Emma Thompson', service: 'Day Pass', status: 'confirmed' },
      { id: 4, time: '11:00 AM', guest: 'David Chen', service: 'Single EES', status: 'confirmed' }
    ],
    '2025-01-27': [
      { id: 5, time: '01:00 PM', guest: 'Lisa Wang', service: 'Single EES', status: 'confirmed' }
    ]
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateKey);
  };

  const getSessionsForDate = (dateKey) => {
    return sessions[dateKey] || [];
  };

  const hasSessionsOnDate = (day) => {
    const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return sessions[dateKey] && sessions[dateKey].length > 0;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      confirmed: 'bg-success text-white',
      pending: 'bg-warning-500 text-white',
      cancelled: 'bg-error text-white'
    };
    return statusColors[status] || 'bg-secondary text-white';
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const selectedSessions = selectedDate ? getSessionsForDate(selectedDate) : [];

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">EES Session Calendar</h2>
          <button
            onClick={() => setShowNewSession(true)}
            className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-all duration-200"
          >
            <Icon name="Plus" size={16} />
            <span>New Session</span>
          </button>
        </div>

        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-secondary-100 rounded-md transition-colors duration-200"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          <h3 className="text-lg font-semibold text-text-primary">
            {monthNames[currentMonth]} {currentYear}
          </h3>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-secondary-100 rounded-md transition-colors duration-200"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-text-secondary">
              {day}
            </div>
          ))}
          
          {/* Empty cells for first week */}
          {[...Array(firstDay)].map((_, index) => (
            <div key={index} className="p-3"></div>
          ))}
          
          {/* Calendar Days */}
          {[...Array(daysInMonth)].map((_, index) => {
            const day = index + 1;
            const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const hasSessions = hasSessionsOnDate(day);
            const isSelected = selectedDate === dateKey;
            const isToday = currentDate.getDate() === day && 
                           currentDate.getMonth() === currentMonth && 
                           currentDate.getFullYear() === currentYear;
            
            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                className={`relative p-3 text-center rounded-md transition-all duration-200 ${
                  isSelected
                    ? 'bg-primary text-white'
                    : isToday
                    ? 'bg-accent-100 text-accent-600 font-semibold'
                    : hasSessions
                    ? 'bg-success-100 text-success-600 hover:bg-success-200' :'hover:bg-secondary-100 text-text-primary'
                }`}
              >
                <span className="text-sm">{day}</span>
                {hasSessions && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                    <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-white' : 'bg-success'}`}></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Session Details */}
      {selectedDate && (
        <div className="p-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Sessions for {new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h3>
          
          {selectedSessions.length > 0 ? (
            <div className="space-y-3">
              {selectedSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:shadow-elevation-1 transition-all duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-text-primary">{session.time}</span>
                      <span className="text-sm text-text-secondary">{session.service}</span>
                    </div>
                    <div className="h-8 w-px bg-border"></div>
                    <div className="flex flex-col">
                      <span className="font-medium text-text-primary">{session.guest}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                        {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-text-secondary hover:bg-secondary-100 rounded-md transition-all duration-200">
                      <Icon name="MessageCircle" size={16} />
                    </button>
                    <button className="p-2 text-text-secondary hover:bg-secondary-100 rounded-md transition-all duration-200">
                      <Icon name="Edit" size={16} />
                    </button>
                    <button className="p-2 text-text-secondary hover:bg-secondary-100 rounded-md transition-all duration-200">
                      <Icon name="MoreVertical" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="Calendar" size={48} className="text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">No sessions scheduled for this date</p>
              <button
                onClick={() => setShowNewSession(true)}
                className="mt-3 text-primary hover:text-primary-700 font-medium"
              >
                Schedule a session
              </button>
            </div>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-3 bg-secondary-50 border-t border-border">
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">24</div>
          <div className="text-xs text-text-secondary">This Month</div>
        </div>
        <div className="p-4 text-center border-x border-border">
          <div className="text-2xl font-bold text-success">7</div>
          <div className="text-xs text-text-secondary">This Week</div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-primary">3</div>
          <div className="text-xs text-text-secondary">Today</div>
        </div>
      </div>
    </div>
  );
};

export default EESSchedulingCalendar;